import { NoResults, BusyIndicator } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { HubDataSource, HubItemCategoryModel } from "client/Api/Hub/hub";
import { RemoteDataSourceState, useDeepComparison } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import {
	ReactElement,
	useMemo,
	useState,
	useEffect,
	useCallback
} from "react";
import { ActiveFilter, HubItemIsNewFilter } from "../../common";
import { EventDateFilterType, EventPreviewModel } from "client/Api/Hub/Events/eventViewer";
import { EventsFilter, useDataSource } from "../Overview/useDataSource";
import { List } from "../Overview/List/List";
import { DataSourceSortableState } from "client/Data/sorting";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	HubEvent,
	subscribe,
	unsubscribe
} from "../../events";

const PAGE_SIZE = 24;

export type CardOverviewProps = {
	activeFilter: ActiveFilter | null;
	dateFilter: EventDateFilterType;
	sorting: DataSourceSortableState<EventPreviewModel>;
	searchTerm: string;
	shouldReserveSpaceForFloatingActionButton: boolean;
	getCategory: (id: number | nil) => HubItemCategoryModel | undefined;
	onNavigateToItem: (id: number) => void;
	onActiveFilterChange: (filter: ActiveFilter | null) => void;
	onSelectedCategoryIdChange: (id: number) => void;
	selectedCategoryId: number | null;
	timezone: TimeZone;
	isNewFilter: HubItemIsNewFilter;
};

export const CardOverview = (props: CardOverviewProps): ReactElement => {
	const {
		searchTerm,
		sorting,
		dateFilter,
		onNavigateToItem,
		getCategory,
		onActiveFilterChange,
		onSelectedCategoryIdChange,
		activeFilter,
		selectedCategoryId,
		shouldReserveSpaceForFloatingActionButton,
		timezone,
		isNewFilter
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [newStatusClearedItemIds, setNewStatusClearedItemIds] = useState<Set<number>>(new Set());

	const filtering = useMemo(
		(): EventsFilter => {
			return {
				categoryId: selectedCategoryId,
				activeFilter,
				dateFilter,
				isNewFilter
			};
		},
		[
			selectedCategoryId,
			activeFilter,
			dateFilter,
			isNewFilter
		]
	);

	const filtersVersion: number = useDeepComparison([
		searchTerm,
		filtering,
		sorting
	]);

	useEffect(
		() => {
			setDesiredAmountOfItems(PAGE_SIZE);
			setNewStatusClearedItemIds(new Set());
		},
		[filtersVersion]
	);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		hasLazyLoadError,
		retryToLoadLastPage,
		reload
	} = useDataSource({
		desiredAmountOfItems,
		filtering,
		searchTerm,
		sorting
	});

	useEffect(
		(): (() => void) => {
			const isNewStatusClearedToken: PubSubSubscriptionToken = subscribe(
				HubEvent.IsNewStatusCleared,
				(_, data) => {
					if (data.kind !== HubDataSource.Event) {
						return;
					}

					setNewStatusClearedItemIds(
						viewedItemIds => {
							const newSet: Set<number> = new Set(viewedItemIds);
							return newSet.add(data.itemId);
						}
					);
				}
			);

			return (): void => unsubscribe(isNewStatusClearedToken);
		},
		[]
	);

	const currenItemsToRender= useMemo(
		(): readonly EventPreviewModel[] => {
			return currentItems.map(
				x => newStatusClearedItemIds.has(x.id)
					? {
						...x,
						isNew: false
					}
					: x
			);
		},
		[
			currentItems,
			newStatusClearedItemIds
		]
	);

	const loadingState: RemoteDataSourceState = getLoadingState();

	const increaseAmount = useCallback(
		(): void => {
			const isLoading = loadingState === "loading";

			if (!isLoading
				&& hasMoreOnServer
				&& !hasLazyLoadError
			) {
				setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
			}
		},
		[
			hasLazyLoadError,
			hasMoreOnServer,
			loadingState
		]
	);

	if (currenItemsToRender.length > 0) {
		return (
			<>
				<List
					items={currenItemsToRender}
					onNavigateToItem={onNavigateToItem}
					getCategory={getCategory}
					onActiveFilterChange={onActiveFilterChange}
					onSelectedCategoryIdChange={onSelectedCategoryIdChange}
					shouldReserveSpaceForFloatingActionButton={shouldReserveSpaceForFloatingActionButton}
					hasMoreItems={hasMoreOnServer && !hasLazyLoadError}
					onLoadmore={increaseAmount}
					isBusy={loadingState === "loading"}
					timezone={timezone}
					kind="overview"
				/>
				{hasLazyLoadError && (
					<NoResults
						trackingId="has-lazy-loading-error"
						icon="gear"
						title={localize("_NotAbleToRetrieveEvents")}
						message={localize("_WeWereUnableToRetrieveYourEvents")}
						onRetry={retryToLoadLastPage}
						hasExtraTopPadding
					/>
				)}
				{loadingState === "loading" && <BusyIndicator />}
			</>
		);
	}

	switch (loadingState) {
		case "loading":
			return <BusyIndicator />;
		case "error":
			return (
				<NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_NotAbleToRetrieveEvents")}
					message={localize("_WeWereUnableToRetrieveYourEvents")}
					onRetry={reload}
					height="fill"
				/>
			);
		default:
	}

	if (searchTerm) {
		return (
			<NoResults
				trackingId="no-search-results"
				icon="magnifying-glass"
				title={localize("_NoSearchResults")}
				message={localize("_PleaseTrySearchingWithAnotherTerm")}
				height="fill"
			/>
		);
	}

	return (
		<NoResults
			trackingId="no-events-yet"
			icon="calendar-star"
			title={localize("_NoEventsYet")}
			message={getNoEventsMessage(activeFilter, selectedCategoryId)}
			height="fill"
		/>
	);
};

CardOverview.displayName = "CardOverview";

function getNoEventsMessage(
	filter: ActiveFilter | null,
	categoryFilter: number | null
): string {
	let keySelection: Record<0 | 1, LocalizationKey>;

	if (filter?.kind === "author") {
		keySelection = {
			[0]: "_TheAuthorDidNotPublishAnyItemsYet",
			[1]: "_TheAuthorDidNotPublishAnyItemsInThisCategoryYet"
		};
	} else if (filter?.kind === "tag") {
		keySelection = {
			[0]: "_ThereAreNoPublishedItemsWithThisTag",
			[1]: "_ThereAreNoPublishedItemsWithThisTagInThisCategory"
		};
	} else {
		keySelection = {
			[0]: "_ThereAreNoPublishedItemsYet",
			[1]: "_ThereAreNoPublishedItemsInThisCategoryYet"
		};
	}

	return localize(
		keySelection[isNil(categoryFilter) ? 0 : 1]
	);
}
